import { useEffect, useState } from 'react';
import axios from 'axios';
import { Palette, ImagePlus, Plus, Trash2 } from 'lucide-react';
import { DashboardLayout } from '../components/layout/DashboardLayout';
import { Header } from '../components/layout/Header';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Switch } from '../components/ui/switch';
import { Textarea } from '../components/ui/textarea';

const API = `${process.env.REACT_APP_BACKEND_URL || "http://localhost:8000"}/api`;
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || 'http://localhost:8000';
const createEmptyTrainer = () => ({
  name: '',
  specialty: '',
  image: '',
  social: { instagram: '#', twitter: '#', youtube: '#' },
});
const createEmptyFacilityImage = () => ({
  url: '',
  alt: '',
});

const DEFAULT_TRAINERS = [
  {
    name: 'Marcus Vance',
    specialty: 'Weight Lifting & Power',
    image: '',
    social: { instagram: '#', twitter: '#', youtube: '#' },
  },
  {
    name: 'Sarah Jenkins',
    specialty: 'HIIT & Agility',
    image: '',
    social: { instagram: '#', twitter: '#', youtube: '#' },
  },
  {
    name: 'David Cross',
    specialty: 'Yoga & Mobility',
    image: '',
    social: { instagram: '#', twitter: '#', youtube: '#' },
  },
];
const DEFAULT_FACILITIES_IMAGES = [
  { url: 'https://images.pexels.com/photos/32610337/pexels-photo-32610337.jpeg', alt: 'Modern gym setup' },
  { url: 'https://images.pexels.com/photos/4162551/pexels-photo-4162551.jpeg', alt: 'Gym locker room' },
  { url: 'https://images.unsplash.com/photo-1770625468069-9393585d7113?w=600', alt: 'Sauna interior' },
];

const DEFAULT_SITE_CONTENT = {
  brand_name: 'FITPULSE PRO',
  hero_overline: 'Welcome to FitPulse Pro',
  hero_title_line_1: 'Transform Your Body,',
  hero_title_highlight: 'Transform Your Life',
  hero_description:
    'Join the ultimate fitness experience with state-of-the-art facilities, expert trainers, and a community that pushes you to be your best.',
  active_message_enabled: false,
  active_message_title: 'Pesan Aktif',
  active_message_body: '',
  logo_path: '',
  facilities_overline: 'Our Space',
  facilities_title_prefix: 'World-Class',
  facilities_title_highlight: 'Facilities',
  facilities_description: 'Ruang latihan, recovery, dan ambience premium yang dirancang agar pengalaman datang ke gym terasa eksklusif.',
  facilities_badge_text: 'Black Fit Space',
  facilities_images: DEFAULT_FACILITIES_IMAGES,
  footer_description: 'Premium training environment untuk performa, recovery, dan transformasi tubuh dengan standar pengalaman yang lebih eksklusif.',
  footer_instagram_url: '#',
  footer_tiktok_url: '#',
  footer_youtube_url: '#',
  footer_newsletter_title: 'Newsletter',
  footer_newsletter_description: 'Get the latest updates, tips, and exclusive offers.',
  footer_copyright_text: '© {year} FitPulse Pro. All rights reserved.',
  contact_map_query: 'Pangkalpinang, Bangka Belitung, Indonesia',
  contact_map_embed_url: 'https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d127670.65595989!2d106.05858565!3d-2.12878945!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x2e22c77c4a8b2a8f%3A0x1c2e0b8a3b8b8b8b!2sPangkalpinang%2C%20Bangka%20Belitung%2C%20Indonesia!5e0!3m2!1sen!2sid!4v1234567890',
  contact_visit_title: 'Visit Us',
  contact_place_name: 'FitPulse Pro Gym',
  contact_address: 'Jl. Sudirman No. 123\nPangkalpinang, Bangka Belitung\nIndonesia 33149',
  contact_phone_display: '+62 717 123 4567',
  contact_phone_link: 'tel:+627171234567',
  contact_email: 'info@fitpulsepro.com',
  trainers: DEFAULT_TRAINERS,
};

function normalizeTrainers(input) {
  if (!Array.isArray(input)) return DEFAULT_TRAINERS.map((trainer) => ({ ...trainer }));
  const normalized = input
    .map((item) => {
      const social = item?.social && typeof item.social === 'object' ? item.social : {};
      return {
        name: (item?.name || '').toString(),
        specialty: (item?.specialty || '').toString(),
        image: (item?.image || '').toString(),
        social: {
          instagram: (social.instagram || '#').toString(),
          twitter: (social.twitter || '#').toString(),
          youtube: (social.youtube || '#').toString(),
        },
      };
    });
  return normalized.length > 0 ? normalized.slice(0, 12) : DEFAULT_TRAINERS.map((trainer) => ({ ...trainer }));
}

function normalizeFacilitiesImages(input) {
  if (!Array.isArray(input)) return DEFAULT_FACILITIES_IMAGES.map((item) => ({ ...item }));
  const normalized = input
    .map((item) => ({
      url: (item?.url || '').toString(),
      alt: (item?.alt || '').toString(),
    }))
    .filter((item) => item.url.trim() || item.alt.trim());
  return normalized.length > 0 ? normalized.slice(0, 12) : DEFAULT_FACILITIES_IMAGES.map((item) => ({ ...item }));
}

function resolveMediaUrl(path) {
  if (!path) return null;
  if (/^https?:\/\//i.test(path)) return path;
  return `${BACKEND_URL}${path.startsWith('/') ? path : `/${path}`}`;
}

export default function Branding() {
  const [siteContent, setSiteContent] = useState(DEFAULT_SITE_CONTENT);
  const [isSavingSiteContent, setIsSavingSiteContent] = useState(false);
  const [isUploadingLogo, setIsUploadingLogo] = useState(false);
  const [uploadingTrainerIndex, setUploadingTrainerIndex] = useState(null);
  const [uploadingFacilityIndex, setUploadingFacilityIndex] = useState(null);

  useEffect(() => {
    const fetchSiteContent = async () => {
      try {
        const { data } = await axios.get(`${API}/site-content`, { withCredentials: true });
        setSiteContent({
          ...DEFAULT_SITE_CONTENT,
          ...(data || {}),
          facilities_images: normalizeFacilitiesImages(data?.facilities_images),
          trainers: normalizeTrainers(data?.trainers),
        });
      } catch (error) {
        console.error('Failed to fetch site content:', error);
        setSiteContent(DEFAULT_SITE_CONTENT);
      }
    };

    fetchSiteContent();
  }, []);

  const handleLogoUpload = async (event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setIsUploadingLogo(true);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/uploads/branding-logo`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      setSiteContent((prev) => ({ ...prev, logo_path: data?.path || '' }));
    } catch (error) {
      console.error('Failed to upload logo:', error);
      alert(error.response?.data?.detail || 'Failed to upload logo');
    } finally {
      setIsUploadingLogo(false);
    }
  };

  const handleSaveSiteContent = async () => {
    const payload = {
      brand_name: (siteContent.brand_name || '').trim(),
      hero_overline: (siteContent.hero_overline || '').trim(),
      hero_title_line_1: (siteContent.hero_title_line_1 || '').trim(),
      hero_title_highlight: (siteContent.hero_title_highlight || '').trim(),
      hero_description: (siteContent.hero_description || '').trim(),
      active_message_enabled: !!siteContent.active_message_enabled,
      active_message_title: (siteContent.active_message_title || '').trim(),
      active_message_body: (siteContent.active_message_body || '').trim(),
      logo_path: (siteContent.logo_path || '').trim() || null,
      facilities_overline: (siteContent.facilities_overline || '').trim(),
      facilities_title_prefix: (siteContent.facilities_title_prefix || '').trim(),
      facilities_title_highlight: (siteContent.facilities_title_highlight || '').trim(),
      facilities_description: (siteContent.facilities_description || '').trim(),
      facilities_badge_text: (siteContent.facilities_badge_text || '').trim(),
      facilities_images: normalizeFacilitiesImages(siteContent.facilities_images).map((item) => ({
        url: item.url.trim(),
        alt: item.alt.trim(),
      })),
      footer_description: (siteContent.footer_description || '').trim(),
      footer_instagram_url: (siteContent.footer_instagram_url || '#').trim() || '#',
      footer_tiktok_url: (siteContent.footer_tiktok_url || '#').trim() || '#',
      footer_youtube_url: (siteContent.footer_youtube_url || '#').trim() || '#',
      footer_newsletter_title: (siteContent.footer_newsletter_title || '').trim(),
      footer_newsletter_description: (siteContent.footer_newsletter_description || '').trim(),
      footer_copyright_text: (siteContent.footer_copyright_text || '').trim(),
      contact_map_query: (siteContent.contact_map_query || '').trim(),
      contact_map_embed_url: (siteContent.contact_map_embed_url || '').trim(),
      contact_visit_title: (siteContent.contact_visit_title || '').trim(),
      contact_place_name: (siteContent.contact_place_name || '').trim(),
      contact_address: (siteContent.contact_address || '').trim(),
      contact_phone_display: (siteContent.contact_phone_display || '').trim(),
      contact_phone_link: (siteContent.contact_phone_link || '').trim(),
      contact_email: (siteContent.contact_email || '').trim(),
      trainers: normalizeTrainers(siteContent.trainers).map((trainer) => ({
        name: trainer.name.trim(),
        specialty: trainer.specialty.trim(),
        image: trainer.image.trim(),
        social: {
          instagram: (trainer.social?.instagram || '#').trim() || '#',
          twitter: (trainer.social?.twitter || '#').trim() || '#',
          youtube: (trainer.social?.youtube || '#').trim() || '#',
        },
      })),
    };

    if (!payload.hero_overline || !payload.hero_title_line_1 || !payload.hero_title_highlight || !payload.hero_description) {
      alert('Field teks hero wajib diisi.');
      return;
    }

    setIsSavingSiteContent(true);
    try {
      const { data } = await axios.put(`${API}/site-content`, payload, { withCredentials: true });
      setSiteContent({
        ...DEFAULT_SITE_CONTENT,
        ...(data || {}),
        facilities_images: normalizeFacilitiesImages(data?.facilities_images),
        trainers: normalizeTrainers(data?.trainers),
      });
      alert('Konten website berhasil disimpan.');
    } catch (error) {
      console.error('Failed to save site content:', error);
      alert(error.response?.data?.detail || 'Failed to save site content');
    } finally {
      setIsSavingSiteContent(false);
    }
  };

  const handleTrainerImageUpload = async (index, event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploadingTrainerIndex(index);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/uploads/branding-trainer-image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      handleUpdateTrainerField(index, 'image', data?.path || '');
    } catch (error) {
      console.error('Failed to upload trainer image:', error);
      alert(error.response?.data?.detail || 'Failed to upload trainer image');
    } finally {
      setUploadingTrainerIndex(null);
    }
  };

  const handleFacilityImageUpload = async (index, event) => {
    const file = event.target.files?.[0];
    event.target.value = '';
    if (!file) return;

    setUploadingFacilityIndex(index);
    try {
      const formData = new FormData();
      formData.append('file', file);
      const { data } = await axios.post(`${API}/uploads/branding-trainer-image`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' },
      });
      handleUpdateFacilityImageField(index, 'url', data?.path || '');
    } catch (error) {
      console.error('Failed to upload facility image:', error);
      alert(error.response?.data?.detail || 'Failed to upload facility image');
    } finally {
      setUploadingFacilityIndex(null);
    }
  };

  const handleAddTrainer = () => {
    setSiteContent((prev) => ({
      ...prev,
      trainers: [...normalizeTrainers(prev.trainers), createEmptyTrainer()].slice(0, 12),
    }));
  };

  const handleRemoveTrainer = (index) => {
    setSiteContent((prev) => {
      const trainers = normalizeTrainers(prev.trainers).filter((_, currentIndex) => currentIndex !== index);
      return { ...prev, trainers: trainers.length ? trainers : DEFAULT_TRAINERS };
    });
  };

  const handleUpdateTrainerField = (index, field, value) => {
    setSiteContent((prev) => {
      const trainers = normalizeTrainers(prev.trainers).map((trainer, currentIndex) =>
        currentIndex === index ? { ...trainer, [field]: value } : trainer
      );
      return { ...prev, trainers };
    });
  };

  const handleUpdateTrainerSocial = (index, field, value) => {
    setSiteContent((prev) => {
      const trainers = normalizeTrainers(prev.trainers).map((trainer, currentIndex) =>
        currentIndex === index ? { ...trainer, social: { ...(trainer.social || {}), [field]: value } } : trainer
      );
      return { ...prev, trainers };
    });
  };

  const handleAddFacilityImage = () => {
    setSiteContent((prev) => ({
      ...prev,
      facilities_images: [...normalizeFacilitiesImages(prev.facilities_images), createEmptyFacilityImage()].slice(0, 12),
    }));
  };

  const handleRemoveFacilityImage = (index) => {
    setSiteContent((prev) => {
      const items = normalizeFacilitiesImages(prev.facilities_images).filter((_, currentIndex) => currentIndex !== index);
      return { ...prev, facilities_images: items.length ? items : DEFAULT_FACILITIES_IMAGES };
    });
  };

  const handleUpdateFacilityImageField = (index, field, value) => {
    setSiteContent((prev) => {
      const items = normalizeFacilitiesImages(prev.facilities_images).map((item, currentIndex) =>
        currentIndex === index ? { ...item, [field]: value } : item
      );
      return { ...prev, facilities_images: items };
    });
  };

  const trainers = normalizeTrainers(siteContent.trainers);
  const facilitiesImages = normalizeFacilitiesImages(siteContent.facilities_images);

  return (
    <DashboardLayout>
      <Header title="Website Branding" />
      <div className="p-8 max-w-5xl" data-testid="branding-page">
        <div className="bg-zinc-900/50 border border-white/10 rounded-sm">
          <div className="p-6 border-b border-white/10 flex items-center gap-3">
            <Palette className="w-5 h-5 text-[#D4AF37]" strokeWidth={1.5} />
            <div>
              <h3 className="text-lg font-semibold text-white">Branding Website</h3>
              <p className="text-sm text-zinc-500 mt-1">Ubah logo dan tulisan yang tampil di header serta hero halaman utama</p>
            </div>
          </div>
          <div className="items-start p-6 grid grid-cols-1 xl:grid-cols-[1.1fr_0.9fr] gap-6">
            <div className="space-y-5">
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Nama Brand (Opsional)</Label>
                <Input value={siteContent.brand_name} onChange={(e) => setSiteContent({ ...siteContent, brand_name: e.target.value })} placeholder="Kosongkan untuk memakai nama default" className="bg-zinc-900 border-white/10 text-white rounded-sm" data-testid="site-brand-name-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Teks Atas Hero</Label>
                <Input value={siteContent.hero_overline} onChange={(e) => setSiteContent({ ...siteContent, hero_overline: e.target.value })} className="bg-zinc-900 border-white/10 text-white rounded-sm" data-testid="site-hero-overline-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Headline Baris 1</Label>
                <Input value={siteContent.hero_title_line_1} onChange={(e) => setSiteContent({ ...siteContent, hero_title_line_1: e.target.value })} className="bg-zinc-900 border-white/10 text-white rounded-sm" data-testid="site-hero-title-line1-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Headline Highlight</Label>
                <Input value={siteContent.hero_title_highlight} onChange={(e) => setSiteContent({ ...siteContent, hero_title_highlight: e.target.value })} className="bg-zinc-900 border-white/10 text-white rounded-sm" data-testid="site-hero-title-highlight-input" />
              </div>
              <div className="space-y-2">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Deskripsi Hero</Label>
                <Textarea value={siteContent.hero_description} onChange={(e) => setSiteContent({ ...siteContent, hero_description: e.target.value })} className="bg-zinc-900 border-white/10 text-white rounded-sm min-h-[120px]" data-testid="site-hero-description-input" />
              </div>
              <div className="space-y-3">
                <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Logo</Label>
                <div className="flex flex-col gap-2">
                  <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-sm border border-white/10 bg-zinc-900 px-4 py-2 text-sm font-medium text-white hover:bg-zinc-800">
                    <ImagePlus className="w-4 h-4" />
                    {isUploadingLogo ? 'Uploading...' : 'Upload Logo'}
                    <input type="file" accept="image/png,image/jpeg,image/webp,image/svg+xml" className="hidden" onChange={handleLogoUpload} data-testid="site-logo-upload-input" />
                  </label>
                  <p className="text-xs text-zinc-500 break-all" data-testid="site-logo-path-text">
                    {siteContent.logo_path ? `Path logo: ${siteContent.logo_path}` : 'Belum ada logo terupload.'}
                  </p>
                </div>
              </div>
              <div className="rounded-sm border border-white/10 bg-zinc-950/70 p-5 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Pesan Aktif Website</Label>
                    <p className="text-sm text-zinc-500 mt-2">Pesan ini tampil di homepage client dan bisa dipakai untuk promo, pengumuman, atau info penting.</p>
                  </div>
                  <Switch
                    checked={!!siteContent.active_message_enabled}
                    onCheckedChange={(checked) => setSiteContent({ ...siteContent, active_message_enabled: checked })}
                    data-testid="site-active-message-switch"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Judul Pesan</Label>
                  <Input
                    value={siteContent.active_message_title || ''}
                    onChange={(e) => setSiteContent({ ...siteContent, active_message_title: e.target.value })}
                    placeholder="Contoh: Promo Minggu Ini"
                    className="bg-zinc-900 border-white/10 text-white rounded-sm"
                    data-testid="site-active-message-title-input"
                  />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Isi Pesan</Label>
                  <Textarea
                    value={siteContent.active_message_body || ''}
                    onChange={(e) => setSiteContent({ ...siteContent, active_message_body: e.target.value })}
                    placeholder="Tulis pesan yang ingin tampil ke pengunjung website."
                    className="bg-zinc-900 border-white/10 text-white rounded-sm min-h-[100px]"
                    data-testid="site-active-message-body-input"
                  />
                </div>
              </div>
              <div className="rounded-sm border border-white/10 bg-zinc-950/70 p-5 space-y-4" data-testid="facilities-config-section">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">World-Class Facilities</Label>
                    <p className="text-sm text-zinc-500 mt-2">Kelola judul, deskripsi, badge, dan galeri gambar section facilities.</p>
                  </div>
                  <Button
                    type="button"
                    onClick={handleAddFacilityImage}
                    className="rounded-sm border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-2 text-xs text-[#D4AF37] hover:bg-[#D4AF37]/20"
                    data-testid="add-facility-image-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Tambah Gambar
                  </Button>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Overline</Label>
                    <Input value={siteContent.facilities_overline || ''} onChange={(e) => setSiteContent({ ...siteContent, facilities_overline: e.target.value })} className="bg-zinc-900 border-white/10 text-white rounded-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Badge Text</Label>
                    <Input value={siteContent.facilities_badge_text || ''} onChange={(e) => setSiteContent({ ...siteContent, facilities_badge_text: e.target.value })} className="bg-zinc-900 border-white/10 text-white rounded-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Title Prefix</Label>
                    <Input value={siteContent.facilities_title_prefix || ''} onChange={(e) => setSiteContent({ ...siteContent, facilities_title_prefix: e.target.value })} className="bg-zinc-900 border-white/10 text-white rounded-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Title Highlight</Label>
                    <Input value={siteContent.facilities_title_highlight || ''} onChange={(e) => setSiteContent({ ...siteContent, facilities_title_highlight: e.target.value })} className="bg-zinc-900 border-white/10 text-white rounded-sm" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Deskripsi Facilities</Label>
                  <Textarea value={siteContent.facilities_description || ''} onChange={(e) => setSiteContent({ ...siteContent, facilities_description: e.target.value })} className="bg-zinc-900 border-white/10 text-white rounded-sm min-h-[100px]" />
                </div>
                <div className="space-y-3">
                  {facilitiesImages.map((item, index) => (
                    <div key={`facility-${index}`} className="rounded-sm border border-white/10 bg-black/30 p-4 space-y-3">
                      <div className="flex items-center justify-between gap-3">
                        <p className="text-xs uppercase tracking-[0.2em] text-zinc-400">Image #{index + 1}</p>
                        <button
                          type="button"
                          onClick={() => handleRemoveFacilityImage(index)}
                          className="inline-flex items-center gap-1 text-xs text-red-300 hover:text-red-200"
                        >
                          <Trash2 className="w-3.5 h-3.5" />
                          Hapus
                        </button>
                      </div>
                      <div className="grid grid-cols-1 gap-3 xl:grid-cols-[120px_1fr]">
                        <div className="h-24 w-full overflow-hidden rounded-sm border border-white/10 bg-zinc-900">
                          {item.url ? (
                            <img src={resolveMediaUrl(item.url)} alt={item.alt || `Facility ${index + 1}`} className="h-full w-full object-cover" />
                          ) : (
                            <div className="flex h-full items-center justify-center text-[10px] uppercase tracking-[0.2em] text-zinc-500">No Image</div>
                          )}
                        </div>
                        <div className="space-y-2">
                          <label className="inline-flex cursor-pointer items-center justify-center gap-2 rounded-sm border border-white/10 bg-zinc-900 px-3 py-2 text-xs font-medium text-white hover:bg-zinc-800">
                            <ImagePlus className="h-4 w-4" />
                            {uploadingFacilityIndex === index ? 'Uploading...' : 'Upload Gambar'}
                            <input type="file" accept="image/png,image/jpeg,image/webp" className="hidden" onChange={(event) => handleFacilityImageUpload(index, event)} />
                          </label>
                          <Input value={item.url} onChange={(e) => handleUpdateFacilityImageField(index, 'url', e.target.value)} placeholder="/uploads/branding/..." className="bg-zinc-900 border-white/10 text-white rounded-sm" />
                          <Input value={item.alt} onChange={(e) => handleUpdateFacilityImageField(index, 'alt', e.target.value)} placeholder="Alt text gambar" className="bg-zinc-900 border-white/10 text-white rounded-sm" />
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <div className="rounded-sm border border-white/10 bg-zinc-950/70 p-5 space-y-4" data-testid="contact-map-config-section">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Contact & Map</Label>
                  <p className="text-sm text-zinc-500 mt-2">Kelola iframe Google Map dan informasi kartu Visit Us pada section contact.</p>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Map Query (API Key)</Label>
                    <Input value={siteContent.contact_map_query || ''} onChange={(e) => setSiteContent({ ...siteContent, contact_map_query: e.target.value })} placeholder="Pangkalpinang, Bangka Belitung, Indonesia" className="bg-zinc-900 border-white/10 text-white rounded-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Visit Card Title</Label>
                    <Input value={siteContent.contact_visit_title || ''} onChange={(e) => setSiteContent({ ...siteContent, contact_visit_title: e.target.value })} placeholder="Visit Us" className="bg-zinc-900 border-white/10 text-white rounded-sm" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Map Embed URL (Fallback)</Label>
                  <Input value={siteContent.contact_map_embed_url || ''} onChange={(e) => setSiteContent({ ...siteContent, contact_map_embed_url: e.target.value })} placeholder="https://www.google.com/maps/embed?pb=..." className="bg-zinc-900 border-white/10 text-white rounded-sm" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Nama Tempat</Label>
                  <Input value={siteContent.contact_place_name || ''} onChange={(e) => setSiteContent({ ...siteContent, contact_place_name: e.target.value })} placeholder="FitPulse Pro Gym" className="bg-zinc-900 border-white/10 text-white rounded-sm" />
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Alamat (per baris)</Label>
                  <Textarea value={siteContent.contact_address || ''} onChange={(e) => setSiteContent({ ...siteContent, contact_address: e.target.value })} placeholder={'Jl. Sudirman No. 123\nPangkalpinang, Bangka Belitung\nIndonesia 33149'} className="bg-zinc-900 border-white/10 text-white rounded-sm min-h-[90px]" />
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Nomor Telepon (Tampil)</Label>
                    <Input value={siteContent.contact_phone_display || ''} onChange={(e) => setSiteContent({ ...siteContent, contact_phone_display: e.target.value })} placeholder="+62 717 123 4567" className="bg-zinc-900 border-white/10 text-white rounded-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Link Telepon (Opsional)</Label>
                    <Input value={siteContent.contact_phone_link || ''} onChange={(e) => setSiteContent({ ...siteContent, contact_phone_link: e.target.value })} placeholder="tel:+627171234567" className="bg-zinc-900 border-white/10 text-white rounded-sm" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Email</Label>
                  <Input value={siteContent.contact_email || ''} onChange={(e) => setSiteContent({ ...siteContent, contact_email: e.target.value })} placeholder="info@fitpulsepro.com" className="bg-zinc-900 border-white/10 text-white rounded-sm" />
                </div>
              </div>
              <div className="rounded-sm border border-white/10 bg-zinc-950/70 p-5 space-y-4" data-testid="footer-config-section">
                <div>
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Footer Content</Label>
                  <p className="text-sm text-zinc-500 mt-2">Kelola deskripsi, sosial media, dan teks newsletter pada footer website.</p>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Footer Description</Label>
                  <Textarea value={siteContent.footer_description || ''} onChange={(e) => setSiteContent({ ...siteContent, footer_description: e.target.value })} className="bg-zinc-900 border-white/10 text-white rounded-sm min-h-[100px]" />
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-3">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Instagram URL</Label>
                    <Input value={siteContent.footer_instagram_url || ''} onChange={(e) => setSiteContent({ ...siteContent, footer_instagram_url: e.target.value })} className="bg-zinc-900 border-white/10 text-white rounded-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">TikTok URL</Label>
                    <Input value={siteContent.footer_tiktok_url || ''} onChange={(e) => setSiteContent({ ...siteContent, footer_tiktok_url: e.target.value })} className="bg-zinc-900 border-white/10 text-white rounded-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">YouTube URL</Label>
                    <Input value={siteContent.footer_youtube_url || ''} onChange={(e) => setSiteContent({ ...siteContent, footer_youtube_url: e.target.value })} className="bg-zinc-900 border-white/10 text-white rounded-sm" />
                  </div>
                </div>
                <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Newsletter Title</Label>
                    <Input value={siteContent.footer_newsletter_title || ''} onChange={(e) => setSiteContent({ ...siteContent, footer_newsletter_title: e.target.value })} className="bg-zinc-900 border-white/10 text-white rounded-sm" />
                  </div>
                  <div className="space-y-2">
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Copyright Text</Label>
                    <Input value={siteContent.footer_copyright_text || ''} onChange={(e) => setSiteContent({ ...siteContent, footer_copyright_text: e.target.value })} placeholder="Gunakan {year} untuk tahun otomatis" className="bg-zinc-900 border-white/10 text-white rounded-sm" />
                  </div>
                </div>
                <div className="space-y-2">
                  <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Newsletter Description</Label>
                  <Textarea value={siteContent.footer_newsletter_description || ''} onChange={(e) => setSiteContent({ ...siteContent, footer_newsletter_description: e.target.value })} className="bg-zinc-900 border-white/10 text-white rounded-sm min-h-[80px]" />
                </div>
              </div>
              <div className="rounded-sm border border-white/10 bg-zinc-950/70 p-5 space-y-4" data-testid="trainer-config-section">
                <div className="flex items-center justify-between gap-4">
                  <div>
                    <Label className="text-xs uppercase tracking-[0.2em] text-zinc-500">Expert Trainers</Label>
                    <p className="text-sm text-zinc-500 mt-2">Data ini akan tampil di section trainer pada website client.</p>
                  </div>
                  <Button
                    type="button"
                    onClick={handleAddTrainer}
                    className="rounded-sm border border-[#D4AF37]/30 bg-[#D4AF37]/10 px-3 py-2 text-xs text-[#D4AF37] hover:bg-[#D4AF37]/20"
                    data-testid="add-trainer-btn"
                  >
                    <Plus className="w-4 h-4 mr-1" />
                    Tambah
                  </Button>
                </div>
                <div className="max-h-[840px] space-y-3 overflow-y-auto pr-1">
                  {trainers.map((trainer, index) => (
                    <div key={`trainer-${index}`} className="rounded-sm border border-white/10 bg-black/30 p-4" data-testid={`trainer-config-item-${index}`}>
                      <div className="flex flex-col gap-4 2xl:flex-row">
                        <div className="w-full 2xl:w-[220px]">
                          <div className="mb-3 flex items-center justify-between gap-3">
                            <p className="text-xs uppercase tracking-[0.2em] text-zinc-400">Trainer #{index + 1}</p>
                            <button
                              type="button"
                              onClick={() => handleRemoveTrainer(index)}
                              className="inline-flex items-center gap-1 text-xs text-red-300 hover:text-red-200"
                              data-testid={`remove-trainer-btn-${index}`}
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                              Hapus
                            </button>
                          </div>
                          <div className="overflow-hidden rounded-sm border border-white/10 bg-zinc-900">
                            <div className="h-[170px] w-full">
                              {trainer.image ? (
                                <img
                                  src={resolveMediaUrl(trainer.image)}
                                  alt={trainer.name || `Trainer ${index + 1}`}
                                  className="h-full w-full object-cover"
                                />
                              ) : (
                                <div className="flex h-full w-full items-center justify-center text-[10px] uppercase tracking-[0.2em] text-zinc-500">
                                  No Image
                                </div>
                              )}
                            </div>
                            <div className="border-t border-white/10 p-3">
                              <label className="inline-flex w-full cursor-pointer items-center justify-center gap-2 rounded-sm border border-white/10 bg-zinc-900 px-3 py-2 text-xs font-medium text-white hover:bg-zinc-800">
                                <ImagePlus className="h-4 w-4" />
                                {uploadingTrainerIndex === index ? 'Uploading...' : 'Upload Foto'}
                                <input
                                  type="file"
                                  accept="image/png,image/jpeg,image/webp"
                                  className="hidden"
                                  onChange={(event) => handleTrainerImageUpload(index, event)}
                                  data-testid={`trainer-image-upload-${index}`}
                                />
                              </label>
                              <p className="mt-2 break-all text-[11px] text-zinc-500">
                                {trainer.image ? `Path: ${trainer.image}` : 'Belum ada foto trainer terupload.'}
                              </p>
                            </div>
                          </div>
                        </div>

                        <div className="min-w-0 flex-1 space-y-3">
                          <div className="grid grid-cols-1 gap-3 2xl:grid-cols-2">
                            <div className="space-y-2">
                              <Label className="block truncate text-[11px] uppercase tracking-[0.1em] text-zinc-500">Nama</Label>
                              <Input value={trainer.name} onChange={(e) => handleUpdateTrainerField(index, 'name', e.target.value)} className="bg-zinc-900 border-white/10 text-white rounded-sm" data-testid={`trainer-name-input-${index}`} />
                            </div>
                            <div className="space-y-2">
                              <Label className="block truncate text-[11px] uppercase tracking-[0.1em] text-zinc-500">Spesialisasi</Label>
                              <Input value={trainer.specialty} onChange={(e) => handleUpdateTrainerField(index, 'specialty', e.target.value)} className="bg-zinc-900 border-white/10 text-white rounded-sm" data-testid={`trainer-specialty-input-${index}`} />
                            </div>
                          </div>

                          <div className="grid grid-cols-1 gap-3 lg:grid-cols-2">
                            <div className="space-y-2">
                              <Label className="block truncate text-[11px] uppercase tracking-[0.1em] text-zinc-500">Instagram</Label>
                              <Input value={trainer.social?.instagram || ''} onChange={(e) => handleUpdateTrainerSocial(index, 'instagram', e.target.value)} className="bg-zinc-900 border-white/10 text-white rounded-sm" data-testid={`trainer-instagram-input-${index}`} />
                            </div>
                            <div className="space-y-2">
                              <Label className="block truncate text-[11px] uppercase tracking-[0.1em] text-zinc-500">Twitter</Label>
                              <Input value={trainer.social?.twitter || ''} onChange={(e) => handleUpdateTrainerSocial(index, 'twitter', e.target.value)} className="bg-zinc-900 border-white/10 text-white rounded-sm" data-testid={`trainer-twitter-input-${index}`} />
                            </div>
                            <div className="space-y-2">
                              <Label className="block truncate text-[11px] uppercase tracking-[0.1em] text-zinc-500">YouTube</Label>
                              <Input value={trainer.social?.youtube || ''} onChange={(e) => handleUpdateTrainerSocial(index, 'youtube', e.target.value)} className="bg-zinc-900 border-white/10 text-white rounded-sm" data-testid={`trainer-youtube-input-${index}`} />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
              <Button onClick={handleSaveSiteContent} disabled={isSavingSiteContent} className="bg-[#D4AF37] text-black font-semibold hover:bg-[#C59A27] rounded-sm" data-testid="save-site-content-btn">
                {isSavingSiteContent ? 'Menyimpan...' : 'Simpan Branding Website'}
              </Button>
            </div>

            <div className="self-start rounded-sm border border-white/10 bg-black/60 overflow-hidden">
              <div className="p-4 border-b border-white/10">
                <p className="text-sm font-medium text-white">Preview</p>
                <p className="text-xs text-zinc-500 mt-1">Perubahan ini akan tampil di header dan hero website client</p>
              </div>
              <div className="p-5 bg-cover bg-center" style={{ backgroundImage: "linear-gradient(rgba(10,10,10,.72), rgba(10,10,10,.92)), url('https://images.pexels.com/photos/29392546/pexels-photo-29392546.jpeg')" }}>
                <div className="mb-10 flex min-h-[72px] items-center gap-3">
                  {siteContent.logo_path ? (
                    <img src={resolveMediaUrl(siteContent.logo_path)} alt={siteContent.brand_name} className="block h-[60px] w-auto max-w-[300px] shrink-0 object-contain object-left align-middle drop-shadow-[0_0_22px_rgba(212,175,55,0.14)]" />
                  ) : (
                    <div className="w-12 h-12 rounded-sm bg-[#D4AF37]/10 border border-[#D4AF37]/30 flex items-center justify-center text-[#D4AF37] font-bold">
                      FP
                    </div>
                  )}
                  {!siteContent.logo_path && <span className="font-semibold tracking-[0.2em] text-white">{siteContent.brand_name}</span>}
                </div>
                <p className="text-[#D4AF37] text-xs font-bold tracking-[0.25em] uppercase mb-5">{siteContent.hero_overline}</p>
                {siteContent.active_message_enabled && siteContent.active_message_body ? (
                  <div className="mb-5 rounded-sm border border-[#D4AF37]/25 bg-[#D4AF37]/10 p-4">
                    <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#D4AF37]">
                      {siteContent.active_message_title || 'Pesan Aktif'}
                    </p>
                    <p className="mt-2 text-sm leading-relaxed text-zinc-200">{siteContent.active_message_body}</p>
                  </div>
                ) : null}
                <h4 className="text-4xl font-black tracking-tight text-white uppercase leading-none">
                  {siteContent.hero_title_line_1}
                  <span className="block text-[#D4AF37] mt-2">{siteContent.hero_title_highlight}</span>
                </h4>
                <p className="text-zinc-300 mt-6 leading-relaxed">{siteContent.hero_description}</p>
                <div className="mt-8 border-t border-white/10 pt-5">
                  <p className="text-[11px] font-bold uppercase tracking-[0.24em] text-[#D4AF37] mb-3">Expert Trainers Preview</p>
                  <div className="space-y-2">
                    {trainers
                      .slice(0, 3)
                      .map((trainer, index) => (
                        <div key={`preview-${index}-${trainer.name}`} className="flex items-center justify-between text-sm">
                          <span className="text-zinc-100">{trainer.name || `Trainer ${index + 1}`}</span>
                          <span className="text-zinc-400">{trainer.specialty || '-'}</span>
                        </div>
                      ))}
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </DashboardLayout>
  );
}



